import {
  ABSTRACT_DESCRIPTION_TYPE,
  ALIAS_IDENTIFIER_TYPE,
  CATEGORY_SUBJECT_SCHEME,
  COLLECTED_DATE_TYPE,
  COVERAGE_DATE_TYPE,
  INSTRUMENT_SUBJECT_SCHEME,
  KNOWN_SUBJECT_SCHEMES,
  MEASURED_VARIABLE_SUBJECT_SCHEMES,
  ORCID_IDENTIFIER_SCHEME,
  OWNER_CONTRIBUTOR_TYPE,
  PROPOSAL_SUBJECT_SCHEME,
  RELATED_IDENTIFIER_TYPE_DESCRIBED_BY,
  RELATED_IDENTIFIER_TYPE_IS_CITED_BY,
  RELATED_IDENTIFIER_TYPE_NEW_VERSION_OF,
  RELATED_IDENTIFIER_TYPE_PREVIOUS_VERSION_OF,
  RELATED_IDENTIFIER_TYPE_REFERENCES,
  RELATED_IDENTIFIER_TYPE_RELATED_DOI,
  RELEASED_DATE_TYPE,
  ROR_IDENTIFIER_SCHEME,
} from '../constants';
import {
  type DataciteDOIAttributes,
  type DataciteParticipant,
  type DataciteRelatedIdentifier,
  type DataciteSubject,
} from './datacite';
import { type DOI, type DOIParticipant } from './doi';

export function convertDataciteToInternal(
  dataciteObject: DataciteDOIAttributes,
): DOI {
  const {
    doi,
    titles,
    creators,
    contributors,
    publicationYear,
    subjects,
    descriptions,
    publisher,
    dates,
    relatedIdentifiers,
    alternateIdentifiers,
    geoLocations,
    created,
    types,
    rightsList,
  } = dataciteObject;

  const locationPlaces = geoLocations
    ?.map((geoLocation) => geoLocation.geoLocationPlace)
    .filter((place): place is string => !!place);

  return {
    doi,
    title: titles[0].title,
    creators: creators?.map(makeParticipant),
    contributors: contributors?.map(makeParticipant),
    publicationYear: parseIntOrUndefined(publicationYear),
    instruments: subjects
      .filter(
        (subject) =>
          subject.subjectScheme && // eslint-disable-line @typescript-eslint/prefer-optional-chain -- TO BE CHECKED
          subject.subjectScheme.toUpperCase() === INSTRUMENT_SUBJECT_SCHEME,
      )
      .flatMap((subject) => subject.subject.split(',')),
    proposals: subjects
      .filter(
        (subject) =>
          subject.subjectScheme && // eslint-disable-line @typescript-eslint/prefer-optional-chain -- TO BE CHECKED
          subject.subjectScheme.toUpperCase() === PROPOSAL_SUBJECT_SCHEME,
      )
      .flatMap((subject) => subject.subject.split(',')),
    description: descriptions.find(
      (description) =>
        description.descriptionType.toUpperCase() === ABSTRACT_DESCRIPTION_TYPE,
    )?.description,
    publisher,
    collectionDate: dates.find(
      (date) => date.dateType.toUpperCase() === COLLECTED_DATE_TYPE,
    )?.date,
    releaseDate: dates.find(
      (date) => date.dateType.toUpperCase() === RELEASED_DATE_TYPE,
    )?.date,
    categories: subjects
      .filter(
        (subject) =>
          subject.subjectScheme && // eslint-disable-line @typescript-eslint/prefer-optional-chain -- TO BE CHECKED
          subject.subjectScheme.toUpperCase() === CATEGORY_SUBJECT_SCHEME,
      )
      .map((subject) => subject.subject),
    ...deriveRelatedIdentifierFields(relatedIdentifiers),
    resourceType: types.resourceType,
    resourceTypeGeneral: types.resourceTypeGeneral,
    aliases: alternateIdentifiers
      ?.filter(
        (identifier) =>
          identifier.alternateIdentifierType.toUpperCase() ===
          ALIAS_IDENTIFIER_TYPE,
      )
      .map((identifier) => identifier.alternateIdentifier),
    operationalPeriod: dates.find(
      (date) => date.dateType.toUpperCase() === COVERAGE_DATE_TYPE,
    )?.date,
    techniques: subjects
      .filter(
        (subject) =>
          subject.subjectScheme &&
          MEASURED_VARIABLE_SUBJECT_SCHEMES.has(
            subject.subjectScheme.toUpperCase(),
          ),
      )
      .map(makeControlledTerm),
    classifications: subjects
      .filter(
        (subject) =>
          subject.subjectScheme &&
          !KNOWN_SUBJECT_SCHEMES.has(subject.subjectScheme.toUpperCase()),
      )
      .map(makeControlledTerm),
    location: locationPlaces?.length ? locationPlaces.join(', ') : undefined,
    owners: contributors
      ?.filter(
        (contributor) => contributor.contributorType === OWNER_CONTRIBUTOR_TYPE,
      )
      .map((contributor) => {
        const { name, ror } = makeParticipant(contributor);
        return {
          name,
          ror,
          location: contributor.affiliation?.length
            ? contributor.affiliation.map(getAffiliationName).join(', ')
            : undefined,
        };
      }),
    createdDate: created,
    keywords: subjects
      .filter((subject) => !subject.subjectScheme)
      .map((subject) => subject.subject),
    rights: rightsList?.[0]
      ? {
          rights: rightsList[0].rights,
          rightsURI: rightsList[0].rightsUri,
        }
      : undefined,
  };
}

function deriveRelatedIdentifierFields(
  relatedIdentifiers: DataciteRelatedIdentifier[] = [],
) {
  return {
    relatedDOI: relatedIdentifiers
      .filter(
        (relatedIdentifier) =>
          relatedIdentifier.relatedIdentifierType ===
          RELATED_IDENTIFIER_TYPE_RELATED_DOI,
      )
      .map((relatedIdentifier) => relatedIdentifier.relatedIdentifier),
    citationPublicationDOI: relatedIdentifiers.find(
      (relatedIdentifier) =>
        relatedIdentifier.relationType === RELATED_IDENTIFIER_TYPE_IS_CITED_BY,
    )?.relatedIdentifier,
    referenceURLs: relatedIdentifiers
      .filter(
        (relatedIdentifier) =>
          relatedIdentifier.relationType === RELATED_IDENTIFIER_TYPE_REFERENCES,
      )
      .map((relatedIdentifier) => relatedIdentifier.relatedIdentifier),
    describedByDOIs: relatedIdentifiers
      .filter(
        (relatedIdentifier) =>
          relatedIdentifier.relationType ===
          RELATED_IDENTIFIER_TYPE_DESCRIBED_BY,
      )
      .map((relatedIdentifier) => relatedIdentifier.relatedIdentifier),
    isNewVersionOfDOI: relatedIdentifiers.find(
      (relatedIdentifier) =>
        relatedIdentifier.relationType ===
        RELATED_IDENTIFIER_TYPE_NEW_VERSION_OF,
    )?.relatedIdentifier,
    isPreviousVersionOfDOI: relatedIdentifiers.find(
      (relatedIdentifier) =>
        relatedIdentifier.relationType ===
        RELATED_IDENTIFIER_TYPE_PREVIOUS_VERSION_OF,
    )?.relatedIdentifier,
  };
}

function makeParticipant(p: DataciteParticipant): DOIParticipant {
  const orcid = p.nameIdentifiers?.find(
    (i) => i.nameIdentifierScheme.toUpperCase() === ORCID_IDENTIFIER_SCHEME,
  );
  const ror = p.nameIdentifiers?.find(
    (i) => i.nameIdentifierScheme.toUpperCase() === ROR_IDENTIFIER_SCHEME,
  );
  const name =
    p.familyName && p.givenName ? `${p.givenName} ${p.familyName}` : p.name;
  return {
    name,
    orcid: orcid ? orcid.nameIdentifier : undefined,
    ror: ror ? ror.nameIdentifier : undefined,
    contributorType: p.contributorType,
  };
}

function makeControlledTerm(subject: DataciteSubject) {
  return {
    name: subject.subject,
    uri: subject.valueUri,
    scheme: subject.subjectScheme,
  };
}

function getAffiliationName(affiliation: string | { name: string }): string {
  return typeof affiliation === 'string' ? affiliation : affiliation.name;
}

function parseIntOrUndefined(value: string | number | undefined) {
  if (value === undefined) {
    return undefined;
  }
  const res = typeof value === 'string' ? Number.parseInt(value) : value;
  if (Number.isNaN(res)) {
    return undefined;
  }
  return res;
}
